import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  MessageSquare, Copy, Check, MapPin, CheckCircle2, AlertTriangle, Loader2, Send,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { ukDate, ukTime } from '../../lib/format';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stamp = (value) => (value
  ? `${ukDate(value)} ${ukTime(new Date(value).toTimeString().slice(0, 5))}`
  : null);

// The three job-day buttons, plus the ready-made client message
export const JobDayActions = ({ job, token }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [showProblem, setShowProblem] = useState(false);
  const [problem, setProblem] = useState('');
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API}/jobs/${job.id}/day`, { headers });
      setData(res.data);
    } catch (err) {
      setData(null);
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    const refresh = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
    /* eslint-disable-next-line */
  }, [job.id]);

  const record = async (action) => {
    setBusy(action);
    try {
      const res = await axios.post(`${API}/jobs/${job.id}/day/${action}`, {}, { headers });
      toast.success(res.data.job_completed
        ? 'All done — this job is now complete, thank you!'
        : 'Saved, thank you');
      if (action === 'messaged') setShowMessage(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save that');
    } finally {
      setBusy('');
    }
  };

  const sendProblem = async () => {
    setBusy('problem');
    try {
      await axios.post(`${API}/jobs/${job.id}/day/problem`, { note: problem }, { headers });
      toast.success('Thanks, the office has been told straight away');
      setShowProblem(false);
      setProblem('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not report that');
    } finally {
      setBusy('');
    }
  };

  if (!data) return null;

  const { progress, windows, message } = data;

  const Status = ({ label, at }) => (
    <span className="text-[11px] text-slate-500">
      {label} <strong className={at ? 'text-green-700' : 'text-slate-400'}>
        {at ? stamp(at) : 'not yet'}
      </strong>
    </span>
  );

  return (
    <div className="rounded-[10px] border border-hair bg-page p-3" data-testid={`job-day-${job.id}`}>
      <p className="bd-heading mb-2 text-[11px] text-gold-deep">On the day</p>

      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => setShowMessage(true)}
                disabled={!!windows.messaged}
                title={windows.messaged || ''}
                data-testid={`message-client-${job.id}`}>
          <MessageSquare className="mr-1 h-4 w-4" /> Message the client
        </Button>
        <Button size="sm" onClick={() => record('arrived')}
                disabled={!!progress.arrived_at || busy === 'arrived'}
                title={windows.arrived || ''}
                data-testid={`arrived-${job.id}`}>
          {busy === 'arrived' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <MapPin className="mr-1 h-4 w-4" />}
          {progress.arrived_at ? 'Arrived' : 'Arrived'}
        </Button>
        <Button size="sm" onClick={() => record('done')}
                disabled={!!progress.done_at || busy === 'done'}
                title={windows.done || ''}
                data-testid={`all-done-${job.id}`}>
          {busy === 'done' ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            : <CheckCircle2 className="mr-1 h-4 w-4" />}
          All done
        </Button>
        <Button size="sm" variant="ghost" className="text-red-600"
                onClick={() => setShowProblem(true)}
                data-testid={`report-problem-${job.id}`}>
          <AlertTriangle className="mr-1 h-4 w-4" /> Report a problem
        </Button>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        <Status label="Messaged" at={progress.messaged_at} />
        <Status label="Arrived" at={progress.arrived_at} />
        <Status label="All done" at={progress.done_at} />
      </div>

      {(windows.messaged || windows.arrived || windows.done) && (
        <p className="mt-2 text-[11px] text-slate-500">
          {windows.messaged || windows.arrived || windows.done}
        </p>
      )}

      {data.team_size > 1 && !progress.done_at && (
        <p className="mt-1 text-[11px] text-slate-500">
          This job completes once everyone on the team has pressed All done.
        </p>
      )}

      {/* Ready-made client message */}
      <Dialog open={showMessage} onOpenChange={setShowMessage}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Message the client</DialogTitle></DialogHeader>

          {!message.has_phone ? (
            <p className="rounded-[10px] border border-hair bg-cream p-3 text-sm text-ink"
               data-testid={`no-client-phone-${job.id}`}>
              No client phone on this job, please ask the office.
            </p>
          ) : (
            <div className="space-y-3">
              <p className="whitespace-pre-wrap rounded-[10px] border border-hair bg-cream p-3 text-sm text-ink"
                 data-testid={`client-message-text-${job.id}`}>
                {message.text}
              </p>
              <div className="flex flex-wrap gap-2">
                {message.whatsapp_link && (
                  <Button asChild size="sm" data-testid={`whatsapp-${job.id}`}>
                    <a href={message.whatsapp_link} target="_blank" rel="noreferrer">
                      <Send className="mr-1 h-4 w-4" /> WhatsApp
                    </a>
                  </Button>
                )}
                {message.sms_link && (
                  <Button asChild size="sm" variant="outline" data-testid={`sms-${job.id}`}>
                    <a href={message.sms_link}>
                      <MessageSquare className="mr-1 h-4 w-4" /> Text message
                    </a>
                  </Button>
                )}
                <Button size="sm" variant="outline"
                        onClick={() => {
                          navigator.clipboard?.writeText(message.text);
                          setCopied(true);
                          toast.success('Copied');
                        }}
                        data-testid={`copy-message-${job.id}`}>
                  {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
                  Copy
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Sends from your own phone, so the client can reply to you directly.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessage(false)}>Close</Button>
            <Button onClick={() => record('messaged')} disabled={busy === 'messaged'}
                    data-testid={`mark-messaged-${job.id}`}>
              {busy === 'messaged' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Mark as messaged
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report a problem */}
      <Dialog open={showProblem} onOpenChange={setShowProblem}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-base">Report a problem</DialogTitle></DialogHeader>
          <Textarea rows={4} value={problem} onChange={(e) => setProblem(e.target.value)}
                    placeholder="Tell us what's happened and we'll pick it up straight away"
                    data-testid={`problem-note-${job.id}`} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProblem(false)}>Cancel</Button>
            <Button onClick={sendProblem} disabled={!problem.trim() || busy === 'problem'}
                    data-testid={`send-problem-${job.id}`}>
              {busy === 'problem' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Tell the office
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default JobDayActions;
